import { assign, get, includes, isEmpty, keys, omit, isNull } from 'lodash';
import { getPaymentBillingInfo, getPaymentGatewayInfo } from '@/apis/payment';
import { safeJSONParse } from '@/utils';
import { CONTACT_TYPES } from '@/store/modules/address';
import { WISE, achVerifyStatusMap, payMethodMap } from '@/views/checkout/payment/helper/constant';
import MessageBox from '@zoom/zoom-ui/src/components/message-box/message-box';
import { translation } from '@/plugins/i18n';
import Enums from '@/common/enums/Enums';
import { countries4select } from '@/common/regionUtils';
import { apmPaymentType } from '@/views/checkout/payment/helper/util';
import { canUseDefaultPaymentMethod } from '@/utils/inr';
import { $context } from '@/global-install';

export const getDefaultPayMethodInfo = () => {
  return {
    paymentType: '',
    paymentGateway: '',
    paymentResponse: '',
  };
};

export const getDefaultPayApmInfo = () => {
  return {
    isApm: false,
    apmPaymentMethodId: '',
    apmPaymentMethodName: '',
    tileImageFileName: '',
    errorMessage: null,
    submitApm: false,
    // ALTERNATIVE CREDIT CARD FOR ZUORA OUTAGE
    loadApmInIframe: false,
    embedIframeSrcUrl: '',
    isDefaultPayment: false,
    defaultPaymentName: '',
    blockSubmit: false,
    wiseStateValue: '',
    defaultWisePayment: '',
    isPaymentSourceRequired: false,
    klarnaBillingCycleMismatch: false,
    invalidDueToCountryChange: false,
    unsupportedPlan: false
  };
};

export const getDefaultAddressInfo = () => {
  return {
    isSameAsContactSign: true,
    address1: '',
    address2: '',
    zip: '',
    city: '',
    state: '',
    country: '',
  };
};

export const getDefaultCvvReAuthInfo = () => {
  return {
    accountId: null,
    pmId: null,
    isCreditCardReAuthRequired: false,
  };
};

export const PAYMENT_ACTION_TYPES = {
  getPaymentInfo: '/get/payment/info',
  preCheckPayment: 'pre/check/payment',
};

export const ttrTaxStatusList = {
  'EXEMPT': 'EXEMPT',
  'PENDING': 'PENDING',
  'NON_EXEMPT': 'NON_EXEMPT',
  'RETRIEVING': 'RETRIEVING',
};

const state = {
  apmInfo: getDefaultPayApmInfo(),
  paymentMethodInfo: getDefaultPayMethodInfo(),
  cardAddressInfo: getDefaultAddressInfo(),
  payMethodReRenderTimes: 0,
  gatewayInfo: {},
  countryUseAdyenForCardValidation: {},
  billingInfo: {},
  adyenPaymentInfo: {},
  selectedPaymentMethod: null,
  taxInfo: {
    vatNumber: '',
    uin: '',
    notVatRegistered: false,
    ttrTaxExempt: '',
    expiration: '',
  }, // valid sold to tax info
  cvvReAuthInfo: getDefaultCvvReAuthInfo(),
  paymentMethodUpdated: false,
  isEditingPaymentMethod: false,
  inrCheckoutError: false
};

const getters = {
  apmInfo: (state) => state.apmInfo,
  paymentMethodInfo: (state) => state.paymentMethodInfo,
  curPaymentMethod: (state) =>
    state.apmInfo.isDefaultPayment
      ? apmPaymentType(state.apmInfo.apmPaymentMethodName, state.apmInfo.tileImageFileName)
      : state.paymentMethodInfo?.paymentType,
  cardAddressInfo: (state) => state.cardAddressInfo,
  withCcAccount: (state) => !!state.billingInfo?.dfPaymentMethodId,
  withOutCcAccount: (state, getters) => getters.withCcAccount === false,
  payMethodReRenderTimes: (state) => state.payMethodReRenderTimes,
  gatewayInfo: (state) => state.gatewayInfo,
  countryUseAdyenForCardValidation: (state) => state.countryUseAdyenForCardValidation,
  billingInfo: (state) => state.billingInfo,
  isWithoutCC: (state) => {
    return (
      isEmpty(state.curPaymentMethod) &&
      isNull(get(state.billingInfo, 'paymentMethodType', null)) &&
      isEmpty(get(state.billingInfo, 'dfPaymentMethod', null))
    );
  },
  isWithCC_ACH_NonAPM: (state) => {
    return (
      get(state.billingInfo, 'paymentMethodType', null) === payMethodMap.ach &&
      get(state.billingInfo, 'dfPaymentMethod.type', null) === payMethodMap.ach
    );
  },
  isWithCC: (state) => {
    return (
      get(state.billingInfo, 'paymentMethodType', null) === payMethodMap.credit &&
      get(state.billingInfo, 'dfPaymentMethod.type', null) === payMethodMap.credit
    );
  },
  isWithCCByIndia: (state) => {
    return (
      get(state.billingInfo, 'paymentMethodType', null) === payMethodMap.creditByIndia &&
      get(state.billingInfo, 'dfPaymentMethod.type', null) === payMethodMap.creditByIndia
    );
  },
  soldToBaseContact: (state, getters) => {
    const soldToContactInfo = getters.billingInfo?.soldToContact;
    const isSameAsContactSign = getters.cardAddressInfo.isSameAsContactSign;
    const soldContact = (isEmpty(soldToContactInfo) ? getters.account : soldToContactInfo) || {};

    return {
      phone: soldContact.phone,
      firstName: soldContact.firstName,
      lastName: soldContact.lastName,
      email: soldContact.email || soldContact?.workEmail,
      contactSame: isSameAsContactSign,
      type: CONTACT_TYPES.SOLD_TO,
    };
  },
  soldToContact: (state, getters) => {
    return {
      ...getters.soldToBaseContact,
      ...omit(getters.addressInfo, ['coppa']),
    };
  },
  billToBaseContact: (state, getters) => {
    const billToContactInfo = getters.billingInfo?.billToContact;
    const isSameAsContactSign = getters.cardAddressInfo.isSameAsContactSign;
    const billContact = (isEmpty(billToContactInfo) ? getters.account : billToContactInfo) || {};

    return {
      phone: billContact.phone,
      firstName: billContact.firstName,
      lastName: billContact.lastName,
      email: billContact.email || billContact.workEmail,
      contactSame: isSameAsContactSign,
      type: CONTACT_TYPES.BILLING_TO,
    };
  },
  billToContact: (state, getters) => {
    const { contactSame } = getters.billToBaseContact;
    const addressInfo = contactSame ? getters.addressInfo : getters.cardAddressInfo;
    // hack code (if without cc account, must merge soldto several field to billto)
    const { companyName, employeeCount, accountHq } = getters.addressInfo;
    const additionalFields = getters.withCcAccount
      ? {}
      : {
          companyName,
          employeeCount,
          accountHq,
        };

    return {
      ...additionalFields,
      ...getters.billToBaseContact,
      ...omit(addressInfo, ['isSameAsContactSign', 'coppa']),
    };
  },
  taxInfo: (state) => state.taxInfo,
  isTaxExempt: (state, getters) => {
    const { soldToContact } = getters;
    const ECMCountries = ['CA', 'US'];
    if (ECMCountries.includes(soldToContact?.country)) {
      return state.taxInfo.ttrTaxExempt === ttrTaxStatusList.EXEMPT;
    }
    return state.taxInfo.vatNumber && !state.taxInfo.notVatRegistered; // US/CA support currently (TTR)
  },
  adyenPaymentInfo: (state) => state.adyenPaymentInfo,
  selectedPaymentMethod: (state) => state.selectedPaymentMethod,
  cvvReAuthInfo: (state) => state.cvvReAuthInfo,
  paymentMethodUpdated: (state) => state.paymentMethodUpdated,
  isEditingPaymentMethod: (state) => state.isEditingPaymentMethod,
  isKlarnaPaymentEnabled: () => get(window.campaignFlags, 'showKlarnaPayment', false),
  isTeleportedApplePay: (state) => {
    return !state.billingInfo?.zAccountId && !state.billingInfo?.paymentMethodType && state.selectedPaymentMethod == payMethodMap.apple && !!window.ApplePaySession && ($context.is.modal || $context.is.mobile) && (window.campaignFlags?.disableTeleportApplePay !== true);
  },
  inrCheckoutError: (state) => state.inrCheckoutError
};

const mutations = {
  setApmInfo(state, data) {
    state.apmInfo = assign(state.apmInfo, data);
  },
  setPaymentMethodInfo(state, data) {
    state.paymentMethodInfo = assign(state.paymentMethodInfo, data);
  },
  setCardAddressInfo(state, data) {
    if (data?.isSameAsContactSign) {
      state.cardAddressInfo = getDefaultAddressInfo();
    } else {
      const cardAddressFieldList = keys(state.cardAddressInfo);

      keys(data).forEach((fieldName) => {
        includes(cardAddressFieldList, fieldName) && (state.cardAddressInfo[fieldName] = data[fieldName]);
      });
    }
  },
  reRenderPayMethod(state) {
    state.payMethodReRenderTimes++;
  },
  setGatewayInfo(state, data) {
    state.gatewayInfo = data;
  },
  setCountryUseAdyenForCardValidation(state, data) {
    state.countryUseAdyenForCardValidation = data;
  },
  setBillingInfo(state, data) {
    state.billingInfo = data;
  },
  setAdyenPaymentInfo(state, data) {
    state.adyenPaymentInfo = data;
  },
  setSelectedPaymentMethod(state, data) {
    state.selectedPaymentMethod = data;
  },
  setTaxInfo(state, data) {
    state.taxInfo = data;
  },
  setCvvReAuthInfo(state, data) {
    state.cvvReAuthInfo = data;
  },
  setPaymentMethodUpdated(state, data) {
    state.paymentMethodUpdated = data;
  },
  setIsEditingPaymentMethod(state, data) {
    state.isEditingPaymentMethod = data;
  },
  setInrCheckoutError(state, data) {
    state.inrCheckoutError = data
  }
};

const actions = {
  // Update credit card address and bill to contact according to bill to info
  updateCardAddressInfo({ getters, commit }) {
    const { billToContact, soldToSameToBillTo: isSameAsContactSign } = getters.billingInfo;
    const cardAddressInfo = billToContact ? {
      ...billToContact,
      isSameAsContactSign,
      zip: billToContact.postalCode,
      country: get(new Enums(getters.countryMap), [billToContact.country]) || get(countries4select, [billToContact.country]),
    } : getDefaultAddressInfo();

    commit('setCardAddressInfo', cardAddressInfo);
  },
  // Update address info according to sold to info
  updateAddressInfoFromSoldTo({ commit, getters }, soldTo = {}) {
    if (soldTo?.address1 || soldTo?.country) {
      const addressInfo = getters.addressInfo;
      let _form = {};
      keys(soldTo).forEach(k => {
        if (k in addressInfo) _form[k] = soldTo[k];
      });

      _form['zip'] = soldTo['postalCode'];
      _form['country'] = new Enums(getters.countryMap)[soldTo.country] || countries4select[soldTo.country];
      _form['countryName'] = soldTo.country;
      _form['usageType'] = addressInfo?.usageType
      commit('setAddressInfo', { ...addressInfo, ..._form });
    }
  },
  updateAdyenPaymentInfo({ commit }, adyenPaymentInfo) {
    const info = {
      adyenPayConfigData: safeJSONParse(adyenPaymentInfo.adyenPayConfigData),
      adyenPaymentMethodsObject: safeJSONParse(adyenPaymentInfo.adyenPaymentMethodsObject),
      adyenPaymentMethodsEnabled: safeJSONParse(adyenPaymentInfo.adyenPaymentMethodsEnabled),
    };
    commit('setAdyenPaymentInfo', info);
  },
  updateTaxInfo({ getters, commit }) {
    const { notVatRegistered, taxExemptStatus, vatNumber, uin } = getters.billingInfo;
    const taxInfo = {
      vatNumber,
      uin,
      notVatRegistered: notVatRegistered === 'true',
      ttrTaxExempt: taxExemptStatus === 'Yes' ? ttrTaxStatusList.EXEMPT : null,
    };
    commit('setTaxInfo', taxInfo);
  },
  async getPaymentBillingInfo({ commit, dispatch }) {
    const { result } = await getPaymentBillingInfo();
    const billingInfo = result?.billingInfo || {};
    const { soldToContact = '', billToContact, isExternalAPMMethod = false } = billingInfo;
    const { name:companyName , employeeCount ,usageType} = billingInfo;

    if (!billingInfo.dfPaymentMethod) {
      billingInfo.paymentMethodType = null;
    }

    commit('setBillingInfo', billingInfo);
    // update current payment method type
    if (canUseDefaultPaymentMethod()) {
      billingInfo.paymentMethodType && commit('setPaymentMethodInfo', { paymentType: billingInfo.paymentMethodType });
    }

    // update apm info
     if (isExternalAPMMethod) {
      let wisePayment = ''
      if (billingInfo?.dfPaymentMethod?.payment_method_name__c === WISE) {
        const apmDetails = JSON.parse(get(billingInfo, "externalApmPaymentDetails"))
        wisePayment = apmDetails?.wise || ''
      }
      commit('setApmInfo', {
        isApm: true,
        apmPaymentMethodName: billingInfo?.dfPaymentMethod?.payment_method_name__c,
        isDefaultPayment: true,
        defaultPaymentName: billingInfo?.dfPaymentMethod?.payment_method_name__c,
        defaultWisePayment: wisePayment,
        tileImageFileName: wisePayment,
        invalidDueToCountryChange: !billingInfo?.isCurrentCountrySupportDefaultPM || false,
        blockSubmit: billingInfo?.isCurrentCountrySupportDefaultPM === false
      });
     } else {
      commit('setApmInfo', getDefaultPayApmInfo());
     }

    // update credit card address info and bill info
    billToContact && dispatch('updateCardAddressInfo');

    // Update address info according to sold to info
    soldToContact && dispatch('updateAddressInfoFromSoldTo', Object.assign({},soldToContact,{ companyName , employeeCount ,usageType}));

    // update tax info
    billingInfo && dispatch('updateTaxInfo');
  },
  async getPaymentGatewayInfo({ commit, dispatch }) {
    const { result } = await getPaymentGatewayInfo();
    const { gatewayInfo, adyenPaymentInfo = {}, countryUseAdyenForCardValidation  } = result;
    commit('setGatewayInfo', gatewayInfo);
    commit('setCountryUseAdyenForCardValidation', countryUseAdyenForCardValidation);

    // set adyen payment info (for Google Pay / Apple Pay)
    !isEmpty(adyenPaymentInfo) && dispatch('updateAdyenPaymentInfo', adyenPaymentInfo);
  },
  async [PAYMENT_ACTION_TYPES.getPaymentInfo]({ dispatch }) {
    if (window.$context?.is?.loggedIn) {
      return Promise.all([dispatch('getPaymentGatewayInfo'), dispatch('getPaymentBillingInfo')]);
    } else {
      return Promise.all([dispatch('getPaymentGatewayInfo')]);
    }
  },
  [PAYMENT_ACTION_TYPES.preCheckPayment]({ getters }) {
    const currentPayMethodType = get(getters.billingInfo, 'paymentMethodType', '');

    if (currentPayMethodType === payMethodMap.ach) {
      const achVerifyStatus = get(getters.billingInfo, 'dfPaymentMethod.achAccountVerificationStatus__c', '');
      let message = '';

      if (
        achVerifyStatus === achVerifyStatusMap.pending ||
        achVerifyStatus === achVerifyStatusMap.accountVerificationFailed ||
        achVerifyStatus === achVerifyStatusMap.verifyFirstAttemptFailed ||
        achVerifyStatus === achVerifyStatusMap.verifySecondAttemptFailed
      ) {
        message = translation('billing.payment.ach.verify_pending_error');
      } else if (achVerifyStatus === achVerifyStatus.retryExceeded) {
        message = translation('billing.payment.ach.verify_failed');
      }

      if (message) {
        MessageBox.alert(message, {
          message,
          confirmButtonText: translation('common.btn_close'),
          dangerouslyUseHTMLString: true,
        });
        return Promise.reject();
      }
    }

    return Promise.resolve();
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
